import '../../Assets/scripts/api/compare/compare-global-event.js';
import translate from './Assets/translate.js';
import { renderItem, renderItemPlaceholder, renderErrorTemplate } from './Assets/templates.js';
import cancelCompare from '../../Assets/scripts/api/compare/cancel-compare.js';
import getCompareItems from '../../Assets/scripts/api/compare/get-compare-items.js';
import getCompareItemsData from '../../Assets/scripts/api/compare/get-compare-items-data.js';
import getProductDataFromAttributes from '../../Assets/helpers/tracking/get-product-data-from-attributes.js';
import resetCompare from '../../Assets/scripts/api/compare/reset-compare.js';
import { compareItemLimit } from '../../Assets/configuration/index.js';
import trapFocus from './Assets/Focus/trapFocus.js';

/** @typedef {import('../../scripts/api/compare/typedefs.js').CompareItem} CompareItem */

// Feature detect :focus-visible support
const supportsFocusVisible = (() => {
  try {
    return document.createElement('div').matches(':focus-visible');
  } catch {
    return false;
  }
})();

export default class ProductCompareBanner extends HTMLElement {
  #form;
  #countEl;
  #countElSr;
  #containerErrors;
  #openBannerBtn;
  #labelText;
  #submitBtn;
  #itemsList;
  #open = false;
  #hasItems = false;
  #checkboxTarget;
  #skipToCompareLink;

  constructor() {
    super();

    this.#form = this.querySelector('form');
    this.#countEl = this.querySelector('.js-compareBannerCount');
    this.#countElSr = this.querySelector('.js-compareBannerCountSr');
    this.#containerErrors = this.querySelector('.js-compareBannerErrors');
    this.#openBannerBtn = this.querySelector('.js-compareBannerBtn');
    this.#labelText = this.querySelector('.js-compareBannerLabelText');
    this.#submitBtn = this.querySelector('.js-goToCompare');
    this.#itemsList = this.querySelector('.js-compareBannerItems');
    this.#skipToCompareLink = document.querySelector('#skip-to-compare');

    this.#openBannerBtn.addEventListener('click', this.#handleOpenClick.bind(this));
    this.#form.addEventListener('submit', this.#handleSubmit.bind(this));
    this.#form.addEventListener('reset', this.#handleReset.bind(this));
    this.addEventListener('click', this.#handleCancelClick.bind(this));
    this.#skipToCompareLink.addEventListener('click', this.#handleOpenClick.bind(this));

    // Global events listeners
    globalThis.addEventListener('compare', this.#handleCompare.bind(this));
    globalThis.addEventListener('compareerror', this.#handleCompareError.bind(this));
    globalThis.addEventListener('beforecompareitem', this.#getTarget.bind(this));

    const { items } = getCompareItems();
    this.#updateState({ items });
  }

  #getTarget({ detail: { relatedTarget } }) {
    this.#checkboxTarget = relatedTarget;
  }

  get open() {
    return this.#open;
  }

  set open(value) {
    value = Boolean(value);
    if (value === this.#open) return;
    this.#updateOpenState(value);
  }

  // ========== Event Handlers ==========

  #handleOpenClick() {
    this.#updateOpenState(!this.open);
    this.#removeErrorMessage();
  }

  #handleSubmit(event) {
    event.preventDefault();
    if (this.#submitBtn.disabled) return;

    // Récupère les produits depuis les inputs hidden
    const products = [...this.#itemsList.querySelectorAll('input[name="products"]')].map((input) => input.value);

    const action = this.#form.getAttribute('action') || '/comparer';
    const target = this.#form.getAttribute('target') || '_blank';
    const url = `${action}/${products.join('/')}`;

    globalThis.open(url, target);
  }

  #handleReset(event) {
    event.preventDefault();

    const { items } = getCompareItems();

    // Tracking: beforecomparecancel event (cancelable)
    const beforeEvent = new CustomEvent('beforecomparecancel', {
      bubbles: true,
      cancelable: true,
      detail: { items, relatedTarget: event.target }
    });

    if (!this.dispatchEvent(beforeEvent)) {
      return;
    }

    resetCompare();

    // Tracking: compareitemcancel event
    this.dispatchEvent(
      new CustomEvent('compareitemcancel', {
        bubbles: true,
        detail: { items, relatedTarget: event.target }
      })
    );

    globalThis.dispatchEvent(
      new CustomEvent('compare', {
        bubbles: true,
        detail: getCompareItems()
      })
    );
  }

  async #handleCompare({ detail: { items, origin = null } }) {
    this.#removeErrorMessage();
    await this.#updateState({ items });

    if (origin === null) {
      this.#updateOpenState(true);
    }
  }

  #handleCompareError({
    detail: {
      error: { name }
    }
  }) {
    this.#updateErrorState({ name });
    this.#updateOpenState(true);
  }

  #handleCancelClick(event) {
    const cancelCompareBtn = event.target.closest('.js-removeCompareItem');
    if (!cancelCompareBtn) return;

    const { type } = cancelCompareBtn.dataset;

    const items = [
      {
        ...getProductDataFromAttributes(cancelCompareBtn),
        offer: null,
        type
      }
    ];

    // Tracking: beforecomparecancel event (cancelable)
    const beforeEvent = new CustomEvent('beforecomparecancel', {
      bubbles: true,
      cancelable: true,
      detail: { items, relatedTarget: cancelCompareBtn }
    });

    if (!this.dispatchEvent(beforeEvent)) {
      return;
    }

    cancelCompare(...items);

    // Tracking: compareitemcancel event
    this.dispatchEvent(
      new CustomEvent('compareitemcancel', {
        bubbles: true,
        detail: { items, relatedTarget: cancelCompareBtn }
      })
    );

    globalThis.dispatchEvent(
      new CustomEvent('compare', {
        bubbles: true,
        detail: getCompareItems()
      })
    );
  }

  // ========== State Management ==========
  #updateOpenState(open) {
    this.#open = open;
    this.classList.toggle('--opened', this.#hasItems && this.open);
    this.classList.toggle('--hasItems', this.#hasItems);
    this.#openBannerBtn.setAttribute('aria-expanded', this.#open ? 'true' : 'false');
    this.#labelText.textContent = this.#open ? translate('masquer') : translate('voir-le-comparateur');

    if (this.classList.contains('--opened')) {
      this.focus();
      trapFocus(this);
    } else if (this.#checkboxTarget && supportsFocusVisible && this.#checkboxTarget.matches(':focus-visible')) {
      this.#checkboxTarget.querySelector('input')?.focus();
    }

    this.dispatchEvent(
      new CustomEvent(this.open ? 'show' : 'close', {
        bubbles: true
      })
    );
  }

  async #updateState({ items }) {
    const itemsData = items.length > 0 ? await getCompareItemsData({ items }) : [];

    this.#skipToCompareLink.classList.toggle('--hide', items.length === 0);

    const itemCount = itemsData.length;
    const hasItems = (this.#hasItems = itemCount > 0);
    const canSubmit = itemCount >= 1;

    this.#updateOpenState(this.open);

    this.#countEl.innerText = `(${hasItems ? itemCount : '--'}/${compareItemLimit})`;
    this.#countElSr.innerText = `${hasItems ? translate('nombre-produit', itemCount) : ''}`;
    this.#submitBtn.disabled = !canSubmit;
    this.#submitBtn.classList.toggle('--disabled', !canSubmit);

    const renderedItems = itemsData.map((item) => renderItem(item));
    const renderedPlaceholders = Array.from({ length: compareItemLimit - itemCount }).map((_, index) =>
      renderItemPlaceholder(itemCount + index + 1)
    );

    this.#itemsList.innerHTML = [...renderedItems, ...renderedPlaceholders].join('');
  }

  // ========== Error Management ==========

  #removeErrorMessage() {
    this.#containerErrors.querySelector('.f-alert')?.remove();
  }

  #updateErrorState(error) {
    this.#removeErrorMessage();
    if (error == null) return;

    const errorHTML = renderErrorTemplate(error.name);
    this.#containerErrors.insertAdjacentHTML('afterbegin', errorHTML);
  }
}

customElements.get('f-compare-banner') || customElements.define('f-compare-banner', ProductCompareBanner);
